import { useState } from 'react';

interface UseEditableFormProps<T> {
  initialData: T;
  onSave?: (data: T) => Promise<void> | void;
}

export const useEditableForm = <T extends Record<string, any>>({
  initialData,
  onSave,
}: UseEditableFormProps<T>) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<T>(initialData);
  const [isSaving, setIsSaving] = useState(false);

  const handleEditToggle = () => {
    if (isEditing) {
      // Reset form data when canceling
      setFormData(initialData);
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (onSave) {
        await onSave(formData);
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field: keyof T, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  return {
    isEditing,
    formData,
    isSaving,
    handleEditToggle,
    handleSave,
    handleInputChange,
    setFormData,
  };
};
